"use client";

import {
  resolveSunburstArcColor,
  SunburstChart,
} from "@/components/charts/sunburst-chart";
import { SunburstSegment } from "@/components/charts/sunburst-segment";
import { SunburstCenter } from "@/components/charts/sunburst-center";
import { SunburstLabels } from "@/components/charts/sunburst-labels";
import { buildArcs } from "@/components/charts/sunburst";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { cn } from "@/lib/utils";
import { ModuleChartSunburstData } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { chartPaletteStyle } from "../imports-token-scope";
import { JSX, useCallback, useMemo, useState } from "react";
import { CHART_SQUARE_SIZE } from "@/config/layout";

const DEFAULT_DATA: ModuleChartSunburstData = {
  name: "Revenue",
  children: [
    {
      name: "Product",
      children: [
        { name: "Enterprise", value: 198 },
        { name: "Pro", value: 145 },
        { name: "Starter", value: 62 },
      ],
    },
    {
      name: "Services",
      children: [
        { name: "Consulting", value: 88 },
        { name: "Training", value: 42 },
      ],
    },
    {
      name: "Partnerships",
      children: [
        { name: "Referrals", value: 55 },
        { name: "Resellers", value: 38 },
      ],
    },
  ],
};

const CHART_SIZE = CHART_SQUARE_SIZE.sunburst;

export default function ModuleSunburstChart({
  data = DEFAULT_DATA,
  grayscale,
  accent,
  className,
}: {
  data?: ModuleChartSunburstData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const cleanedData = useMemo(() => {
    function clean(node: ModuleChartSunburstData): ModuleChartSunburstData {
      return {
        ...node,
        name: stripInlineMarkdown(node.name),
        children: node.children?.map(clean),
      };
    }
    return clean(data);
  }, [data]);
  const { arcs } = useMemo(() => buildArcs(cleanedData), [cleanedData]);

  // The legend is derived from the arcs it points at, in arc order, so a row's
  // marker cannot end up on a different palette or a different index than its
  // segment.
  const legendArcs = useMemo(() => arcs.filter((a) => a.depth === 1), [arcs]);

  const legendItems = useMemo(() => {
    const total = legendArcs.reduce((sum, a) => sum + a.value, 0);
    return legendArcs.map((arc) => ({
      label: arc.name,
      value: arc.value,
      maxValue: total || 1,
      color: resolveSunburstArcColor(arc),
    }));
  }, [legendArcs]);

  const [chartHovered, setChartHovered] = useState<number | null>(null);
  const [chartSelected, setChartSelected] = useState<number | null>(null);
  const [legendHovered, setLegendHovered] = useState<number | null>(null);
  const { activeIndex, handleSelect, setLabelRef, setActiveIndex } =
    useLegendClickSync();

  // Chart and legend share one selection: whichever was touched last owns it.
  const handleChartSelectChange = useCallback(
    (index: number | null) => {
      setChartSelected(index);
      setActiveIndex(null);
    },
    [setActiveIndex],
  );

  const handleLegendSelect = useCallback(
    (index: number) => {
      setChartSelected(null);
      setChartHovered(null);
      handleSelect(index);
    },
    [handleSelect],
  );

  const legendArcIndex = useMemo(() => {
    const idx = legendHovered ?? activeIndex;
    if (idx === null) return null;
    return legendArcs[idx]?.arcIndex ?? null;
  }, [legendHovered, activeIndex, legendArcs]);

  return (
    <div
      className={cn("flex w-full flex-col gap-3", className)}
      style={chartPaletteStyle(grayscale, accent)}
    >
      <div className="flex w-full items-center justify-center">
        <SunburstChart
          data={cleanedData}
          size={CHART_SIZE}
          hoveredIndex={chartHovered ?? legendArcIndex}
          onHoverChange={setChartHovered}
          selectedIndex={chartSelected}
          onSelectChange={handleChartSelectChange}
        >
          {arcs.map((arc) => (
            <SunburstSegment index={arc.arcIndex} key={arc.id} />
          ))}
          <SunburstCenter />
          <SunburstLabels />
        </SunburstChart>
      </div>

      {legendItems.length > 0 && (
        <SwipeScroll className="w-full pb-1" fade="auto">
          <Legend
            items={legendItems}
            hoveredIndex={legendHovered}
            onHoverChange={setLegendHovered}
            selectedIndex={activeIndex}
            onSelect={handleLegendSelect}
            onLabelRef={setLabelRef}
            className="flex flex-row gap-6"
          >
            <LegendItem className="flex shrink-0 items-center gap-2">
              <LegendMarker className="h-2.5 w-2.5" />
              <LegendLabel />
              <LegendValue
                showPercentage
                className="text-[11px] text-neutral-500 dark:text-neutral-400"
              />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
  );
}
